
import { Component, Prop, Vue } from 'vue-property-decorator';
import { bulkEditActionTitle, formatDateTime as formatDateTimeFn, formatElapsed as formatElapsedFn, formatRelative as formatRelativeFn, isRecent as isRecentFn, STATUS_COLORS } from '@/utils/bulkJob';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import TextField from '@/components/shared/TextField.vue';

@Component<BulkJobHistory>({
	components: {
		PaginationControls,
		TableHeaders,
		TextField
	}
})
export default class BulkJobHistory extends Vue {
	@Prop({ type: Array, required: true }) private jobs!: BulkJob[];
	@Prop({ type: Number, required: true }) private totalCount!: number;
	@Prop({ type: Number, required: true }) private currentPage!: number;
	@Prop({ type: Number, required: true }) private itemsPerPage!: number;
	@Prop({ type: String, required: true }) private sortBy!: string;
	@Prop({ type: String, required: true }) private sortDirection!: 'asc' | 'desc';
	@Prop({ type: String, required: false, default: '' }) private title!: string;
	@Prop({ type: String, required: false, default: 'startedAt' }) private timeSortKey!: string;
	@Prop({ type: String, required: false, default: '' }) private selectedJobId!: string;
	@Prop({ type: Boolean, required: false, default: false }) private isLoading!: boolean;
	@Prop({ type: String, required: false, default: 'bulk-job' }) private idPrefix!: string;
	@Prop({ type: Boolean, required: false, default: false }) private showActionColumn!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private searchable!: boolean;

	private formatElapsed: typeof formatElapsedFn = formatElapsedFn;
	private formatDateTime: typeof formatDateTimeFn = formatDateTimeFn;
	private formatRelative: typeof formatRelativeFn = formatRelativeFn;
	private isRecent: typeof isRecentFn = isRecentFn;
	private searchInput: string = '';

	private get totalPages(): number {
		return Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
	}

	private get tableHeaders(): TableHeader[] {
		const actionHeader: TableHeader[] = this.showActionColumn
			? [{ text: this.$t('shared.bulk_job.history.columns.action') }]
			: [];
		return [
			...actionHeader,
			{
				text: this.$t('shared.bulk_job.history.columns.started_at'),
				name: this.timeSortKey,
				sort: this.sortBy === this.timeSortKey ? this.sortDirection : 'asc'
			},
			{ text: this.$t('shared.bulk_job.history.columns.status') },
			{ text: this.$t('shared.bulk_job.history.columns.elapsed') },
			{ text: this.$t('shared.bulk_job.history.columns.counts') }
		];
	}

	private beforeDestroy(): void {
		cancelDebounce(this.onSearchInput);
	}

	private statusColor(status: BulkJobStatus): string {
		return STATUS_COLORS[status] || 'grey';
	}

	/**
	 * The row's count summary. A bulk item edit reports items — that is the unit the operator
	 * selected — and deliberately reuses the progress card's own key so a job reads identically in
	 * both places. Every other job type (menu pull, replication, create tag) does one unit of work
	 * per location, so it keeps counting locations. Jobs from a backend that predates the item
	 * counters fall through to the same location branch.
	 *
	 * @param {BulkJob} job
	 * @return {string}
	 */
	private countSummary(job: BulkJob): string {
		if (job.itemCounts && job.totalItems) {
			return this.$tc('shared.bulk_job.items_succeeded', job.itemCounts.completed, {
				completed: job.itemCounts.completed,
				total: job.totalItems
			});
		}
		return this.$t('shared.bulk_job.progress.summary', { completed: job.counts.completed, total: job.totalLocations });
	}

	/**
	 * Failures to flag on the row. For an item edit this must count failed *items*: a location whose
	 * items partly failed is still marked `completed` (best-effort keeps the edits that landed), so
	 * `counts.failed` reads 0 and the row would claim a clean run.
	 *
	 * @param {BulkJob} job
	 * @return {number}
	 */
	private failedCount(job: BulkJob): number {
		if (job.itemCounts && job.totalItems) {
			return job.itemCounts.failed;
		}
		return job.counts.failed;
	}

	/**
	 * What the job did ("Assign tags", "Create tag"). Empty for job types that carry neither.
	 *
	 * @param {BulkJob} job
	 * @return {string}
	 */
	private actionTitle(job: BulkJob): string {
		if (job.tagCreate) {
			return this.$t('shared.bulk_job.titles.create_tag');
		}
		return bulkEditActionTitle(job.itemEdit);
	}

	@Debounce(500)
	private onSearchInput(): void {
		this.$emit('search', this.searchInput);
	}

	private clearSearch(): void {
		this.searchInput = '';
		this.$emit('search', '');
	}

	private selectJob(jobId: string): void {
		this.$emit('select', jobId);
	}

	private onToggleSort(columnName: string): void {
		this.$emit('sort', columnName);
	}
}
